// The bubble that appears before the server has heard about it. Lifted as is
// from the Andon app's `lib/optimistic.ts`.
//
// WHY A MESSAGE IS THE ONE THING THE THREAD DRAWS OPTIMISTICALLY
//
// Nothing else is. A take, a close, a cancel — every one of those is a promise
// to a person standing at a stopped machine, and a screen that shows one
// before the server has taken it is a screen that can lie about whether help
// is coming. So they all wait for the round trip, the way every write in this
// app does, and that is deliberate.
//
// A message is different in kind. It changes no state anybody is relying on,
// it is worthless if it is slow, and every messaging app on the planet has
// taught people that the bubble appears when they tap Send. Waiting a round
// trip and a refetch before the words show up does not read as "careful", it
// reads as the app being broken — and the fix people reach for is tapping Send
// again.
//
// SO THE HONESTY IS IN THE DRAWING, NOT IN THE DELAY. A pending bubble is
// visibly pending (see `Bubble` in ThreadView), and if the post fails the
// bubble goes, the words come back into the box, and the files come back into
// the tray. Nothing is silently lost and nothing is silently invented.

import type { Staged } from '@/lib/staging';
import type { ThreadAttachmentVM, ThreadMessageVM } from '@/api/andon/types';

/**
 * Epoch ms → the shape Odoo spells a datetime in.
 *
 * `2026-08-26 04:36:00`: UTC, second-granular, no zone on the end. It has to
 * be this exact shape because the thread parses every timestamp with the same
 * function (`toEpoch`), which appends the `Z` itself — hand it an ISO string
 * with a `T` and a `Z` already in it and the message sorts into next week.
 */
export function odooStamp(at: number): string {
  return new Date(at).toISOString().slice(0, 19).replace('T', ' ');
}

/**
 * A staged file, as the thread draws files.
 *
 * Every id here is real — the filter below keeps anything still uploading or
 * failed out — so the thread's ordinary attachment URL resolves. That is what
 * lets four photos and their caption appear as one finished bubble rather
 * than as a spinner.
 *
 * `audio` becomes `voice`: the tray names a recorder, the thread names a
 * player, and this is the one place the two vocabularies meet.
 */
function asAttachment(file: Staged): ThreadAttachmentVM {
  return {
    id: file.id!,
    name: file.name,
    kind: file.kind === 'audio' ? 'voice' : file.kind,
    // Not known on this side and not drawn from: the thread picks its renderer
    // from `kind`. The real value arrives with the refetch a moment later.
    mimetype: '',
    bytes: file.bytes,
    seconds: file.seconds,
  };
}

/**
 * The message to show while the real one is in flight.
 *
 * The id is NEGATIVE, and that is load-bearing rather than tidy. Odoo ids are
 * positive, so nothing can collide with one — and every server call that takes
 * a message id (reacting to it, for one) would be refused rather than acting on
 * somebody else's row. `Bubble` uses the sign to decide not to offer those at
 * all, which is the belt to this brace.
 */
export function pendingMessage(input: {
  body: string;
  author: string;
  /**
   * Who is writing, so the in-flight bubble wears the same face as the real
   * one that replaces it. The same pair `mine` is decided by: the employee on
   * a PIN device, the user on a personal sign-in.
   */
  authorUid?: number | null;
  authorEmployeeId?: number | null;
  files: Staged[];
  /** Epoch ms, from the caller so this stays testable. */
  at: number;
  /**
   * How many files went with this message but had not finished uploading.
   *
   * SAID, NOT DRAWN, and that is the honest option of the two. Send does not
   * wait for uploads (see `staging.canSend`), so a bubble can go up while its
   * video is still travelling — and an attachment tile with no id behind it
   * is a permanently broken image, which is the exact failure the filter below
   * exists to prevent. So the bubble carries a count instead, the thread says
   * "1 still uploading", and the real tile appears when the refetch brings the
   * message back with the file on it.
   */
  uploading?: number;
}): ThreadMessageVM {
  return {
    id: -input.at,
    // ALWAYS `normal`. The two other shapes are written by the server on
    // somebody's behalf — the raise bubble by the raise itself, the decision by
    // the close — and neither has a composer in front of it to draw an
    // optimistic version of.
    kind: 'normal',
    body: input.body,
    author: input.author,
    authorUid: input.authorUid ?? null,
    authorEmployeeId: input.authorEmployeeId ?? null,
    at: odooStamp(input.at),
    // Always. The only person who can be looking at a bubble this app has not
    // sent yet is the person who wrote it.
    mine: true,
    attachments: input.files.filter((f) => f.id != null && f.error == null).map(asAttachment),
    // Nobody can have reacted to something that has not arrived.
    reactions: [],
    pending: true,
    ...(input.uploading ? { uploading: input.uploading } : {}),
  };
}
